import { Injectable } from "@angular/core";
import { AbstractControl, ValidationErrors, ValidatorFn } from "@angular/forms";
import { firstValueFrom } from "rxjs";
import { HttpResult } from "../../core/services/base-http.service";
import { StorageService } from "../../core/services/storage.service";

import { failurePaymentUpgradeResultModel, ticketColorsModal } from "../ticket-booking/ticket-booking.model";

// Define a type for ticketColorsModal for better type safety
type TicketNote = { line1?: string; line2?: string };
export type TicketColorModalType = Record<string, { ticketNote?: TicketNote }>;

import {
  APIErrorModel,
  BaseResponse,
} from "../../shared/models/base-response.model";
import { TicketsApiService } from "./tickets-api.service";
import {
  AssignChildTicketRequest,
  AssignTicketRequest,
  AssignTicketResponse,
  GetImageUploadLinkDataModel,
  InitiateUpgradePaymentResModel,
  RazorpayUpgradeRequest,
  TicketModel,
  TicketSuggestionResponse,
  UpgradeTicketMediaPayload,
  UpgradeTicketMediaResponse,
  UploadData,
  VerifyUpgradePaymentRequest,
} from "./tickets.model";
import * as domtoimage from "dom-to-image-more";
import { RazorpayService } from "../../core/services/razorpay.service";
declare var Razorpay: any;

@Injectable({
  providedIn: "root",
})
export class TicketsBllService {
  constructor(
    private api: TicketsApiService,
    private storageService: StorageService,
    private razorpaySdk: RazorpayService

  ) {
    // Initialize any required services or variables here
  }
  private modelsLoaded = false;

  /**
   * Custom validator to check if the name contains only valid characters
   * @returns Validator function
   */
  nameValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value || "";
      const allowedSpecialChars = [".", "-", "'", "’"];
      const isAllowedChar = (char: string) =>
        /^[\p{L}\p{M}\d]$/u.test(char) ||
        char === " " ||
        allowedSpecialChars.includes(char) ||
        char === "\u200D";

      const hasInvalidChars = [...value].some((char) => !isAllowedChar(char));
      const hasConsecutiveSpecials = /([.\-’'])\1+/.test(value);
      const hasConsecutiveSpaces = /\s{2,}/.test(value); // detects 2 or more spaces in a row
      const hasNumbers = /\d/.test(value);

      if (hasInvalidChars) {
        return { invalidName: true };
      }
      if (hasConsecutiveSpecials) {
        return { consecutiveSpecials: true };
      }
      if (hasConsecutiveSpaces) {
        return { invalidName: true };
      }
      if (hasNumbers) {
        return { invalidName: true }; // specifically numbers
      }

      return null; // valid
    };
  }

  /**
   * Custom validator to ensure the first character is a letter
   * @returns Validator function
   */
  startsWithLetterValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let value = control.value as string;
      if (!value) {
        return null;
      }

      const trimmedValue = value.replace(/^\s+/, "");
      if (trimmedValue !== value) {
        control.setValue(trimmedValue, { emitEvent: false });
        value = trimmedValue;
      }

      const firstChar = value.charAt(0);
      const isValid = /^[\p{L}\p{M}]$/u.test(firstChar);
      return isValid ? null : { startsWithLetter: true };
    };
  }

  getDiscountAmount() {
    return this.storageService.getPaymentDiscount();
  }

  clearPaymentDiscount() {
    this.storageService.getPaymentDiscount();
  }

  savePaymentSuccessFlag(flag: string) {
    this.storageService.savePaymentSuccessFlag(flag);
  }
  clearPaymentSuccessFlagStorage() {
    this.storageService.savePaymentSuccessFlag("0");
  }

  /**
   * @method assignTicket
   * @description Assigns a ticket using ticketCode from storage. Returns success message.
   * @param {AssignTicketRequest} payload - { mobile, fname, lname, state, city, image_rl }
   * @returns {Promise<string>} Success message (or throws on error).
   */
  async assignTicket(
    payload: AssignTicketRequest | AssignChildTicketRequest,
    ticket_code: string
  ) {
    const ticketCode = ticket_code;
    // If ticketCode is missing, throw an error immediately.
    if (!ticketCode) {
      throw new Error("Ticket code is required to assign the ticket.");
    }

    const result: HttpResult<BaseResponse<AssignTicketResponse>> =
      await firstValueFrom(this.api.assignTicketApi(ticketCode, payload));

    const { status, body } = result;

    if (status === 200) {
      return body;
    } else {
      throw <APIErrorModel>{
        status,
        message: body.message,
        data: body.data,
      };
    }
  }

  /**
   * Fetches ticket suggestions from the API.
   */
  async fetchTicketSuggestions(): Promise<
    BaseResponse<TicketSuggestionResponse>
  > {
    // Convert the Observable<HttpResult<...>> to a Promise using firstValueFrom
    const result: HttpResult<BaseResponse<TicketSuggestionResponse>> =
      await firstValueFrom(this.api.getSuggestions());

    // Destructure the HttpResponse-like wrapper from your ApiService
    const { status, body } = result;

    // Treat HTTP 200 as success.
    if (status === 200) {
      // Return the server body as-is so the caller decides how to use body.data/message.
      return body;
    }

    // For non-200 statuses, throw a typed error that upstream code can catch and branch on.
    throw <APIErrorModel>{
      status,
      message: body?.message ?? "Suggestions fetch failed",
      data: body?.data,
    };
  }

  async getTicketList(assigned?: number): Promise<BaseResponse<TicketModel[]>> {
    const summaryId: string | null = this.storageService.getSummaryId();

    const result: HttpResult<BaseResponse<TicketModel[]>> =
      await firstValueFrom(this.api.getTicketsApi(summaryId, assigned));

    const { status, body } = result;

    if (status === 200) {
      return body;
    } else {
      throw <APIErrorModel>{
        status,
        message: body.message,
        data: body.data,
      };
    }
  }

  // Clear the payment success flag
  clearPaymentSuccessFlag(): void {
    this.storageService.savePaymentSuccessFlag("false");
  }

  // Get the payment success flag ("true" or "false")
  getPaymentSuccessFlag(): string | null {
    return this.storageService.getPaymentSuccessFlag();
  }

  // Get the suggestion flag ("1" for used, "0" for not used)
  getUseSuggestion(): string | null {
    return this.storageService.getUseSuggestion();
  }

  // Save the suggestion flag ("1" for used, "0" for not used)
  saveUseSuggestion(flag: string): void {
    this.storageService.saveUseSuggestion(flag);
  }

  // Reset the suggestion flag to "0" (not used)
  resetSuggestionFlag(): void {
    this.storageService.saveUseSuggestion("0");
  }

  //--------------------------------
  //saveShowPaymentSuccess
  saveShowPaymentSuccessPopupFlag(flag: string): void {
    this.storageService.saveShowPaymentSuccess(flag);
  }

  //clearShowPaymentSuccess
  clearPaymentSuccessPopupFlag(): void {
    this.storageService.saveShowPaymentSuccess("false");
  }

  saveUpgradeDiscountAmount(amount: string): void {
    this.storageService.saveDiscountAmount(amount);
  }

  //flag reset
  resetUpgradeDiscountAmount(): void {
    this.storageService.saveDiscountAmount("0");
  }

  /**
   * Stores a mapping of ticket type codes to their associated notes.
   * Each entry contains two lines of note text (`line1` and `line2`) for a ticket.
   * If `null`, no ticket notes are currently loaded.
   */
  private _ticketNoteMap: TicketColorModalType | undefined;

  /**
   * Returns a map of ticket notes for each ticket type.
   * The result is cached after the first call for performance.
   * If ticketColorsModal changes at runtime, this cache will not update automatically.
   * Each note contains 'line1' and 'line2' properties, defaulting to empty strings if undefined.
   *
   * @returns {TicketColorModalType} A map of ticket type codes to their notes.
   */
  get ticketNoteMap(): TicketColorModalType {
    if (!this._ticketNoteMap) {
      this._ticketNoteMap = Object.keys(ticketColorsModal).reduce(
        (acc, tktTypeKey) => {
          const note = (ticketColorsModal as TicketColorModalType)[tktTypeKey]
            ?.ticketNote;
          acc[tktTypeKey] = {
            ticketNote: {
              line1: note?.line1 ?? "",
              line2: note?.line2 ?? "",
            },
          };
          return acc;
        },
        {} as TicketColorModalType
      );
    }
    return this._ticketNoteMap;
  }

  /**
   * Retrieves the image upload link for a specific challenge.
   *
   * @param title The title of the challenge.
   * @param ticket_code The code of the ticket.
   * @returns A promise that resolves to the image upload link.
   */
  async getImageUploadLinkChallenge(title: string, ticket_code: string) {
    const payload: GetImageUploadLinkDataModel = {
      title: title,
    };

    // Send HTTP request and wait for the unified response containing status and body
    const result: HttpResult<BaseResponse<UploadData>> = await firstValueFrom(
      this.api.getUploadImageInfoApi(payload, ticket_code)
    );

    const { status, body } = result;

    if (status === 200) {
      return body;
    } else {
      // Throw error with status and message if request fails
      throw <APIErrorModel>{
        status,
        message: body.message,
        data: body.data,
      };
    }
  }

  /**
   * Fetches detailed information about a specific ticket using its ticket code.
   *
   * This method calls the backend service to retrieve detailed information for the ticket identified by the provided ticket code.
   * It returns a promise that resolves to a `TicketModel` object if the request is successful.
   * If the request fails or the server returns a non-200 status, the promise is rejected with an error message.
   *
   * @param {string} ticketCode - The unique code of the ticket to fetch details for.
   * @returns {Promise<TicketModel>} A promise that resolves to the ticket details.
   * @throws {Error} If the API call fails or returns a non-200 status code.
   */
  async getTicketDetails(ticketCode: string) {
    const result: HttpResult<BaseResponse<TicketModel>> = await firstValueFrom(
      this.api.getTicketDetail(ticketCode)
    );
    const { status, body } = result;

    if (status === 200) {
      // Return data if status is OK

      return body;
    } else {
      throw <APIErrorModel>{
        status,
        message: body.message,
        data: body.data,
      };
    }
  }

  /**
   * Captures the given HTML element as a PNG Data URL.
   */
  private async captureElementAsPng(
    element: HTMLElement,
    scale: number = 2
  ): Promise<string> {
    const style = {
      transform: `scale(${scale})`,
      transformOrigin: "top left",
      width: `${element.offsetWidth}px`,
      height: `${element.offsetHeight}px`,
    };

    const param = {
      width: element.offsetWidth * scale,
      height: element.offsetHeight * scale,
      style,
      cacheBust: true, // prevent cached resources
      useCORS: true, // try to load cross-origin resources via CORS
      filter: (node: any) => {
        // Ignore link or style nodes from external origins
        if (node.tagName === "LINK" || node.tagName === "STYLE") {
          return !/googleapis|gstatic|jsdelivr/.test(node.href || "");
        }
        return true;
      },
    };

    try {
      return await domtoimage.toPng(element, param);
    } catch (err) {
      console.error("Error capturing element as PNG:", err);
      throw err;
    }
  }

  /**
   * Converts a Data URL (base64) to a File object.
   */
  private dataUrlToFile(dataUrl: string, fileName: string): File {
    const arr = dataUrl.split(",");
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : "application/octet-stream";
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], fileName, { type: mime });
  }

  /**
   * Shares or downloads the ticket based on mode.
   * @param element HTML element to capture
   * @param ticketCode Ticket code for file name & caption
   * @param mode 'share' | 'download'
   */
  async handleTicketAction(
    element: HTMLElement,
    ticketCode: string,
    mode: "share" | "download"
  ): Promise<void> {
    try {
      const dataUrl = await this.captureElementAsPng(element);

      if (mode === "download") {
        this.triggerDownload(dataUrl, `ticket-${ticketCode}.png`);
        return;
      }

      if (mode === "share") {
        const file = this.dataUrlToFile(dataUrl, `ticket-${ticketCode}.png`);
        const caption = `KISAN: India's Largest Agri Show, 10-14 Dec 2025, PIECC, Moshi, Pune. Visit KISAN.me`;

        if (navigator.share) {
          await navigator.share({
            files: [file],
            title: "My KISAN Ticket",
            text: caption,
          });
          console.log("Shared successfully!");
        } else {
          console.log("Failed to share!");
        }
      }
    } catch (err) {
      console.error("Ticket action failed:", err);
    }
  }

  /**
   * Triggers the download of an image from a data URL.
   *
   * @param {string} dataUrl - The data URL of the image to be downloaded.
   * @param {string} fileName - The name of the downloaded file.
   */
  private triggerDownload(dataUrl: string, fileName: string): void {
    const link = document.createElement("a");
    link.download = fileName;
    link.href = dataUrl;
    link.click();
  }

  /**
   * Upload image to S3 using API layer and handle response
   */
  async uploadImageToS3(
    mediaUrl: string,
    fields: Record<string, string>,
    file: File
  ) {
    const result: HttpResult<any> = await firstValueFrom(
      this.api.uploadLogoToS3Api(mediaUrl, fields, file)
    );

    const { status, body } = result;
    return status;
  }

  /**
   * Cleans the summary ID from storage.
   */
  callClearSummaryId(): void {
    this.storageService.clearSummaryId();
  }

  /**
     * Initiates payment using Razorpay.
     * @param data - The payment data.
     * @param onSuccess - Callback for successful payment.
     * @param onFailure - Callback for failed payment.
     */
  async payWithRazor(
    name: string,
    mobile: string,
    data: InitiateUpgradePaymentResModel,
    onSuccess: (response: any) => void,
    onFailure: (error: any) => void
  ): Promise<void> {
    return this.razorpaySdk.openCheckout({
      order: data, // backend initiatePaymentResModel
      customer: { name, mobile },
      onSuccess,
      onFailure,
      themeColor: '#008940',
    });
  }


  // Initiate an upgrade payment with the backend.
  // Sends the upgrade option and ticket code to the server, which creates a Razorpay order.
  // On HTTP 200, returns the parsed response body. On non-200, throws an `APIErrorModel` with status/message/data.
  async initiateUpgradePayment(ticketCode: string, data: RazorpayUpgradeRequest) {
    const result: HttpResult<BaseResponse<InitiateUpgradePaymentResModel>> =
      await firstValueFrom(this.api.initiateUpgradePayment(ticketCode, data));

    const { status, body } = result;

    if (status === 200) {
      return body; // BaseResponse<InitiateUpgradePaymentResModel>
    }

    // ✅ Preserve server payload for UI (order id, amount, etc.)
    if (status === 409) {
      throw <APIErrorModel>{
        status,
        message: body?.message || "Payment already initiated, please wait for 15 minutes to initiate again",
        data: body?.data, // <-- IMPORTANT: keep conflict data
      };
    }

    // ✅ Other errors
    throw <APIErrorModel>{
      status,
      message: body?.message || "Something went wrong",
      data: body?.data,
    };
  }




  // Handles failed upgrade payment scenarios.
  // Records payment failure for ticket booking.
  failurePaymentUpgrade(payload: failurePaymentUpgradeResultModel, ticketCode: string): Promise<string> {
    return firstValueFrom(this.api.failedUpgradePayment(ticketCode, payload))
      .then(
        (result: HttpResult<BaseResponse<any>>) => {
          const { status, body } = result;

          if (status === 200) {
            // Return success message (fallback provided)
            return body?.message ?? "Failure reported successfully";
          } else {
            // Non-200 handled as error
            throw new Error(
              `${status}:${body?.message || "Reporting failed"}`
            );
          }
        }
      )
      .catch((error: any) => {
        // Prefer server message if present
        const serverMsg =
          error?.error?.message || error?.message || "Unknown error";
        throw new Error("Failed to report payment failure: " + serverMsg);
      });
  }

  /**
   * Verify an upgrade payment with the backend.
   * Sends the Razorpay confirmation payload to the server, which validates the
   * payment signature and updates the ticket. On HTTP 200, returns the parsed
   * response body. On non-200, throws an `APIErrorModel` with status/message/data.
   */
  async verifyUpgradePayment(
    ticketCode: string,
    data: VerifyUpgradePaymentRequest
  ) {
    const result: HttpResult<BaseResponse<any>> = await firstValueFrom(
      this.api.verifyUpgradePayment(ticketCode, data)
    );
    const { status, body } = result;

    // On HTTP 200, return the body as-is
    if (status === 200) {
      return body;
    } else {
      // On non-200, throw an error with status/message/data
      throw <APIErrorModel>{
        status,
        message: body.message,
        data: body.data,
      };
    }
  }

  /**
   * Copies the provided text to the clipboard.
   * @param text The text to be copied.
   */
  copyToClipboard(text: string) {
    if (navigator.clipboard && window.isSecureContext) {
      // Modern approach: Clipboard API
      navigator.clipboard
        .writeText(text)
        .then(() => console.log("Text copied to clipboard!"))
        .catch((err) => console.error("Failed to copy: ", err));
    } else {
      // Fallback for older browsers
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed"; // avoid scrolling to bottom
      textarea.style.top = "-9999px"; // hide it off-screen
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      try {
        document.execCommand("copy");
        console.log("Text copied using fallback!");
      } catch (err) {
        console.error("Fallback: Could not copy text", err);
      }
      document.body.removeChild(textarea);
    }
  }

  async loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous"; // Handle CORS
      img.src = src;
      img.onload = () => resolve(img);
      img.onerror = (err) => reject(new Error(`Failed to load image: ${src}`));
    });
  }

  async convertImageUrlToBase64(imageUrl: string): Promise<string> {
    return fetch(imageUrl, { mode: "cors" })
      .then((response) => response.blob())
      .then(
        (blob) =>
          new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          })
      );
  }
  clearInviteAccepted(): void {
    this.storageService.saveIsInviteAccepted("");
  }
  isInviteAccepted(): string | null {
    return this.storageService.getIsInviteAccepted();
  }

  async upgradeTicketImage(ticketcode: string, payload: UpgradeTicketMediaPayload): Promise<string> {
    const result: HttpResult<BaseResponse<UpgradeTicketMediaResponse>> = await firstValueFrom(
      this.api.updateImageforUpgradeTicket(ticketcode, payload)
    );
    const { status, body } = result;

    if (status === 200) {
      return body.message;
    } else {
      throw <APIErrorModel>{
        status,
        message: body.message
      };
    }
  }
}
